import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Send as SendIcon,
} from '@mui/icons-material';
import api from '@/services/api';
import Footer from '@/components/Footer';

const FONT = '"Inter", system-ui, -apple-system, sans-serif';
const ACCENT = '#10b981';
const ACCENT_DARK = '#059669';
const pill = { borderRadius: '999px', textTransform: 'none' as const, fontFamily: FONT, fontWeight: 600, px: 3.5 };

const ContactPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await api.post('/contact', { name: name.trim(), email: email.trim(), message: message.trim() });
      setSuccess(true);
      setName('');
      setEmail('');
      setMessage('');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: 'white', fontFamily: FONT }}>
      {/* ─── Hero ───────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 10, md: 14 }, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 0,
          background: `
            radial-gradient(ellipse 80% 60% at 50% 30%, rgba(16,185,129,0.06) 0%, transparent 60%),
            linear-gradient(to bottom, #fafbfc 0%, white 100%)
          `,
        }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Typography
            variant="h1"
            sx={{
              fontFamily: FONT, fontWeight: 800,
              fontSize: { xs: '2.2rem', md: '3rem' },
              lineHeight: 1.1, letterSpacing: '-0.03em',
              color: '#0f172a', mb: 2,
            }}
          >
            Get in touch
          </Typography>
          <Typography sx={{
            fontFamily: FONT, fontSize: { xs: '1.05rem', md: '1.2rem' },
            color: 'text.secondary', lineHeight: 1.6, maxWidth: 560, mx: 'auto',
          }}>
            Have a question, suggestion, or just want to say hello? We'd love to hear from&nbsp;you.
          </Typography>
        </Container>
      </Box>

      {/* ─── Contact form ───────────────────────────────────────── */}
      <Container maxWidth="sm" sx={{ pb: { xs: 10, md: 14 } }}>
        {success ? (
          <Box sx={{
            bgcolor: 'white', borderRadius: '12px', p: { xs: 4, md: 5 },
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
            textAlign: 'center',
          }}>
            <Alert severity="success" sx={{ mb: 3, fontFamily: FONT }}>
              Message sent successfully!
            </Alert>
            <Typography sx={{ fontFamily: FONT, fontSize: '1rem', color: 'text.secondary', lineHeight: 1.6 }}>
              Thanks for reaching out. We'll get back to you as soon as we can.
            </Typography>
            <Button
              sx={{ ...pill, mt: 3, color: ACCENT, border: `1px solid ${ACCENT}`, '&:hover': { bgcolor: `${ACCENT}08` } }}
              onClick={() => setSuccess(false)}
            >
              Send another message
            </Button>
          </Box>
        ) : (
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              bgcolor: 'white', borderRadius: '12px', p: { xs: 4, md: 5 },
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
              display: 'flex', flexDirection: 'column', gap: 3,
            }}
          >
            {error && <Alert severity="error" sx={{ fontFamily: FONT }}>{error}</Alert>}

            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
              fullWidth
              required
              InputProps={{ sx: { fontFamily: FONT } }}
              InputLabelProps={{ sx: { fontFamily: FONT } }}
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              fullWidth
              required
              InputProps={{ sx: { fontFamily: FONT } }}
              InputLabelProps={{ sx: { fontFamily: FONT } }}
            />
            <TextField
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={loading}
              fullWidth
              required
              multiline
              rows={5}
              InputProps={{ sx: { fontFamily: FONT } }}
              InputLabelProps={{ sx: { fontFamily: FONT } }}
            />
            <Button
              type="submit"
              size="large"
              variant="contained"
              disabled={loading}
              endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              sx={{
                ...pill, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK },
                boxShadow: `0 4px 14px ${ACCENT}40`, py: 1.5, fontSize: '1rem',
                alignSelf: 'flex-start',
              }}
            >
              {loading ? 'Sending...' : 'Send message'}
            </Button>
          </Box>
        )}
      </Container>

      <Footer />
    </Box>
  );
};

export default ContactPage;
